import { useNotificationPreferencesStore } from './notificationPreferencesStore';
import { create } from 'zustand';
import { toast } from 'react-hot-toast';
import { Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { AppUserProfile, Tenant, TenantStatus, User, Permission } from '@/types';
import { DEFAULT_PERMISSIONS } from '@/lib/permissions';
import { useLojaAtivaStore } from './lojaAtivaStore';
import { clearCachedToken, fetchWithAuth, primeAuthToken } from '@/services/api';
import { clearLeadMessagesCache } from '@/lib/query/queryClient';
import { clearConversationsCache } from '@/lib/query/useConversations';
import { clearLeadsCache } from '@/lib/query/useLeadsQuery';

export interface AuthState {
  session: Session | null;
  profile: AppUserProfile | null;
  tenant: Tenant | null;
  loading: boolean;
  error: string | null;

  // Derived state properties
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  tenantStatus: TenantStatus | null;
  canAccessApp: boolean;

  // Compatibility property
  user: User | null;

  initialize: () => Promise<void>;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  setError: (error: string | null) => void;
  hasPermission: (perm: Permission) => boolean;
}

const computeDerived = (session: Session | null, profile: AppUserProfile | null, tenant: Tenant | null) => {
  const isAuthenticated = !!session && !!profile;
  const isSuperAdmin = profile?.role === 'super_admin' || profile?.is_platform_owner === true;
  const tenantStatus = tenant?.status ?? null;
  // S2.A-SECURITY: alinhado com o gate do authMiddleware no backend.
  // Super admins (platform owners sem tenant) entram direto. Demais usuários
  // só têm canAccessApp=true quando seu tenant está aprovado.
  const canAccessApp = isAuthenticated && (isSuperAdmin || tenant?.status === 'aprovado');

  const user: User | null = profile ? {
    id: profile.auth_user_id || profile.id,
    tenant_id: profile.tenant_id || 'default',
    nome: profile.nome,
    email: profile.email,
    role: profile.role as any
  } : null;

  return {
    session,
    profile,
    tenant,
    isAuthenticated,
    isSuperAdmin,
    tenantStatus,
    canAccessApp,
    user
  };
};

async function fetchUserProfile(authUserId: string, email?: string, userObject?: any): Promise<AppUserProfile | null> {
  try {
    // 1. Tentar obter o perfil pela API do backend (/api/profile) para garantir que RLS não bloqueie e venha atualizado
    try {
      const apiRes = await fetchWithAuth('/api/profile');
      if (apiRes.ok) {
        const apiProfile = await apiRes.json();
        if (apiProfile && apiProfile.auth_user_id === authUserId) {
          return apiProfile as AppUserProfile;
        }
      }
    } catch (apiErr) {
      console.warn('[AUTH] Falha ao buscar perfil via API, usando fallback do Supabase client:', apiErr);
    }

    let data: any = null;

    // 1. Tentar por auth_user_id
    if (authUserId) {
      const { data: dataByAuth, error } = await supabase
        .from('crm_app_users')
        .select('*')
        .eq('auth_user_id', authUserId)
        .maybeSingle();

      if (!error && dataByAuth) {
        data = dataByAuth;
      }
    }

    // 2. Se não encontrou por auth_user_id e temos e-mail, tentar por email
    if (!data && email) {
      const { data: dataByEmail } = await supabase
        .from('crm_app_users')
        .select('*')
        .eq('email', email.toLowerCase().trim())
        .maybeSingle();

      if (dataByEmail) {
        data = dataByEmail;
        // Vincular auth_user_id em segundo plano
        if (authUserId) {
          supabase
            .from('crm_app_users')
            .update({ auth_user_id: authUserId })
            .eq('id', dataByEmail.id)
            .then(() => console.log('[AUTH] Vinculado auth_user_id para:', email));
        }
      }
    }

    // 3. Se ainda não encontrou, tentar por id direto
    if (!data && authUserId) {
      const { data: dataById } = await supabase
        .from('crm_app_users')
        .select('*')
        .eq('id', authUserId)
        .maybeSingle();

      if (dataById) {
        data = dataById;
      }
    }

    // 4. Se ainda não encontrou no banco, criar dinamicamente/fallback virtual
    if (!data && authUserId) {
      console.log('[AUTH] Perfil do usuário não encontrado. Criando perfil em crm_app_users...');
      const finalEmail = (email || userObject?.email || '').toLowerCase().trim();
      let finalRole = userObject?.app_metadata?.app_role;
      if (!finalRole) {
        // S1-SECURITY: nunca inferir super_admin por substring de email.
        // Um email tipo "vendedor.admin@empresa.com" não vira admin.
        // Privileged fallback claims come only from server-controlled app_metadata.
        // user_metadata is editable by the account, including preference saves.
        finalRole = 'vendedor';
      }
      const finalNome = userObject?.user_metadata?.nome || userObject?.user_metadata?.name || finalEmail.split('@')[0] || 'Usuário';
      const tenantId = userObject?.app_metadata?.tenant_id || null;
      const permissions = DEFAULT_PERMISSIONS[finalRole] || DEFAULT_PERMISSIONS['vendedor'];

      const { data: insertedData, error: insertError } = await supabase
        .from('crm_app_users')
        .insert({
          auth_user_id: authUserId,
          nome: finalNome,
          email: finalEmail,
          role: finalRole,
          tenant_id: tenantId,
          ativo: true,
          permissions,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .maybeSingle();

      if (!insertError && insertedData) {
        console.log('[AUTH] Perfil do crm_app_users criado com sucesso:', insertedData);
        data = insertedData;
      } else {
        console.warn('[AUTH] Falha ao persistir perfil no banco (pode ser RLS ou restrição). Usando fallback virtual:', insertError?.message);
        data = {
          id: authUserId,
          auth_user_id: authUserId,
          nome: finalNome,
          email: finalEmail,
          role: finalRole,
          tenant_id: tenantId,
          loja_id: null,
          ativo: true,
          permissions,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      }
    }

    if (!data) return null;

    const profile = data as AppUserProfile;
    const hasPerms = profile.permissions && typeof profile.permissions === 'object' && Object.keys(profile.permissions).length > 0;
    if (profile.role === 'super_admin' || profile.role === 'tenant_admin') {
      profile.permissions = DEFAULT_PERMISSIONS[profile.role] || DEFAULT_PERMISSIONS['super_admin'];
    } else if (!hasPerms && profile.role) {
      profile.permissions = DEFAULT_PERMISSIONS[profile.role] || DEFAULT_PERMISSIONS['vendedor'];
    }
    return profile;
  } catch (err) {
    console.warn('[AUTH] Falha na requisição crm_app_users:', err);
    return null;
  }
}

async function fetchTenant(tenantId: string | null): Promise<Tenant | null> {
  if (!tenantId) return null;
  try {
    const { data, error } = await supabase
      .from('crm_tenants')
      .select('*')
      .eq('id', tenantId)
      .maybeSingle();

    if (error) {
      console.warn('[AUTH] Erro ao buscar crm_tenants:', error.message);
      return null;
    }
    return data as Tenant | null;
  } catch (err) {
    console.warn('[AUTH] Falha na requisição crm_tenants:', err);
    return null;
  }
}

function translateErrorMessage(msg: string): string {
  const lower = msg.toLowerCase();
  if (lower.includes('invalid login credentials') || lower.includes('invalid_grant')) {
    return 'E-mail ou senha incorretos';
  }
  if (lower.includes('email not confirmed')) {
    return 'E-mail não confirmado. Verifique sua caixa de entrada.';
  }
  if (lower.includes('user disabled')) {
    return 'Usuário desativado. Entre em contato com o suporte.';
  }
  if (lower.includes('too many requests')) {
    return 'Muitas tentativas. Aguarde alguns instantes.';
  }
  return msg || 'Erro ao efetuar login';
}

let isAuthListenerRegistered = false;

export const useAuthStore = create<AuthState>((set, get) => ({
  session: null,
  profile: null,
  tenant: null,
  loading: true,
  error: null,

  isAuthenticated: false,
  isSuperAdmin: false,
  tenantStatus: null,
  canAccessApp: false,
  user: null,

  isImpersonating: false,
  isImpersonatingLoading: false,
  impersonationTargetName: null,
  originalSuperAdmin: null,

  hasPermission: (perm: Permission) => {
    const profile = get().profile;
    if (!profile) return false;
    if (profile.role === 'super_admin' || profile.role === 'tenant_admin') return true;
    return profile.permissions?.[perm] ?? false;
  },

  setError: (error: string | null) => set({ error }),

  initialize: async () => {
    set({ loading: true, error: null });

    // FIX-C: Timeout interno de 10s — se qualquer chamada demorar demais,
    // força loading=false pra o usuário não ficar preso na tela de loading.
    // O aviso visual do StatusGate aparece aos 6s, o timeout duro vem aos 10s.
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Timeout: inicialização demorou mais de 10 segundos')), 10000);
    });

    const initPromise = (async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.warn('[AUTH] Erro getSession:', error.message);
          throw error;
        }

        if (session?.user) {
          const profile = await fetchUserProfile(session.user.id, session.user.email, session.user);
          if (!profile) {
            throw new Error('Não foi possível carregar o perfil do usuário.');
          }
          
          // Fetch accessible stores for this user
          try {
            await useLojaAtivaStore.getState().fetchMinhasLojas(profile);
          } catch (storeErr) {
            console.error('[AUTH] Erro ao carregar lojas acessíveis:', storeErr);
          }

          // S2.A-FIX: aguardar o tenant ANTES do set, evitando janela em que
          // tenant=null + canAccessApp=false (gate S2.A) pisca a tela de
          // "Configuração Incompleta" para usuários aprovados.
          let tenantData: Tenant | null = null;
          if (profile?.tenant_id) {
            try {
              tenantData = await fetchTenant(profile.tenant_id);
              if (!tenantData && profile.role !== 'super_admin') {
                toast.error('Não foi possível carregar os dados da empresa.');
              }
            } catch (err) {
              console.error('[AUTH] Erro ao carregar tenant:', err);
              if (profile.role !== 'super_admin') {
                toast.error('Não foi possível carregar os dados da empresa.');
              }
            }
          }

          set({
            ...computeDerived(session, profile, tenantData),
            loading: false,
            error: null,
          });
        } else {
          set({ ...computeDerived(null, null, null), loading: false, error: null });
        }
      } catch (err: any) {
        console.error('[AUTH] Exceção durante initialize:', err);
        set({ ...computeDerived(null, null, null), loading: false, error: err?.message || 'Erro ao inicializar sessão' });
      }
    })();

    try {
      await Promise.race([initPromise, timeoutPromise]);
    } catch (timeoutErr: any) {
      // Timeout: força saída do loading pra usuário poder tentar de novo
      console.error('[AUTH] Timeout no initialize (10s):', timeoutErr?.message);
      set({ loading: false, error: 'Tempo de inicialização esgotado. Tente novamente.' });
    }

    // Restaura o lastAuthUserId a partir da sessão recuperada (B2)
    let lastAuthUserId: string | null = get().session?.user?.id ?? null;
    if (!isAuthListenerRegistered) {
      isAuthListenerRegistered = true;
      supabase.auth.onAuthStateChange(async (event, session) => {
        // Re-prima o cache com o token fresco do evento (em vez de só limpar).
        // Assim os fetches disparados por este mesmo evento já saem autenticados.
        primeAuthToken(session?.access_token ?? null);
        if (event === 'SIGNED_OUT') {
          clearLeadMessagesCache();
          clearConversationsCache();
          clearLeadsCache();
          try {
            const { useLeadsStore } = await import('@/store/leadsStore');
            useLeadsStore.getState().stopRealtime?.(true); // Interrompe e limpa ledger (B4)
            useLeadsStore.setState({ leads: [] });
          } catch {
            // fallback silencioso
          }
          lastAuthUserId = null;
          set({ ...computeDerived(null, null, null), loading: false });
        } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          if (session?.user) {
            // Se houve mudança real de usuário autenticado (B2)
            if (lastAuthUserId && lastAuthUserId !== session.user.id) {
              clearLeadMessagesCache();
              clearConversationsCache();
              clearLeadsCache();
              // revogar canal/ledger/scope de u1 ANTES de publicar u2 (B2/B4)
              try {
                const { useLeadsStore } = await import('@/store/leadsStore');
                useLeadsStore.getState().stopRealtime?.(true); 
                useLeadsStore.setState({ leads: [] });
              } catch (importErr) {
                console.warn('[AUTH] Erro ao revogar leadsStore:', importErr);
              }
            }
            lastAuthUserId = session.user.id;
            const profile = await fetchUserProfile(session.user.id, session.user.email, session.user);
            
            // Fetch accessible stores for the logged-in user
            try {
              await useLojaAtivaStore.getState().fetchMinhasLojas(profile);
            } catch (storeErr) {
              console.error('[AUTH] Erro ao carregar lojas acessíveis:', storeErr);
            }

            // S2.A-FIX: aguardar o tenant ANTES do set, evitando janela em que
            // tenant=null + canAccessApp=false (gate S2.A) pisca a tela de
            // "Configuração Incompleta" para usuários aprovados.
            let tenantData: Tenant | null = null;
            if (profile?.tenant_id) {
              try {
                tenantData = await fetchTenant(profile.tenant_id);
                if (!tenantData && profile.role !== 'super_admin') {
                  toast.error('Não foi possível carregar os dados da empresa.');
                }
              } catch (err) {
                console.error('[AUTH] Erro ao carregar tenant:', err);
                if (profile.role !== 'super_admin') {
                  toast.error('Não foi possível carregar os dados da empresa.');
                }
              }
            }

            set({
              ...computeDerived(session, profile, tenantData),
              loading: false,
              error: null,
            });
          }
        }
      });
    }
  },

  login: async (email, password) => {
    set({ loading: true, error: null });

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        const translated = translateErrorMessage(error.message);
        set({ loading: false, error: translated });
        return { success: false, error: translated };
      }

      if (!data.session || !data.user) {
        const errStr = 'Falha ao autenticar sessão';
        set({ loading: false, error: errStr });
        return { success: false, error: errStr };
      }

      const profile = await fetchUserProfile(data.user.id, data.user.email, data.user);

      // S2.A-FIX: aguardar o tenant ANTES do set, evitando janela em que
      // tenant=null + canAccessApp=false (gate S2.A) pisca a tela de
      // "Configuração Incompleta" para usuários aprovados no fluxo de login manual.
      let tenantData: Tenant | null = null;
      if (profile?.tenant_id) {
        try {
          tenantData = await fetchTenant(profile.tenant_id);
          if (!tenantData && profile.role !== 'super_admin') {
            toast.error('Não foi possível carregar os dados da empresa.');
          }
        } catch (err) {
          console.error('[AUTH] Erro ao carregar tenant:', err);
          if (profile.role !== 'super_admin') {
            toast.error('Não foi possível carregar os dados da empresa.');
          }
        }
      }

      set({
        ...computeDerived(data.session, profile, tenantData),
        loading: false,
        error: null
      });

      return { success: true };
    } catch (err: any) {
      const translated = translateErrorMessage(err?.message || 'Erro inesperado no login');
      set({ loading: false, error: translated });
      return { success: false, error: translated };
    }
  },

  logout: async () => {
    try {
      // 1. Clear impersonation backups and all Zustand cache keys from localStorage
      localStorage.removeItem('impersonation_backup');
      const zustandKeys = [
        'task-crm-lojas-cache',
        'task-crm-leads-cache',
        'task-crm-conversations-cache',
        'task-crm-config-cache',
        'task-crm-users-cache',
        'task-crm-theme-cache',
        'task-crm-loja-ativa-cache',
        'loja_ativa_id'
      ];
      for (const key of zustandKeys) {
        localStorage.removeItem(key);
      }
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('sb-') || key.includes('supabase.auth'))) {
          localStorage.removeItem(key);
        }
      }
    } catch (e) {
      console.warn('[AUTH] Erro ao limpar localStorage no logout:', e);
    }

    // 2. Reset stores in memory
    try {
      clearLeadMessagesCache();
      clearConversationsCache();
      clearLeadsCache();
      const { useLojasStore } = await import('@/store/lojasStore');
      const { useLeadsStore } = await import('@/store/leadsStore');
      const { useConversationsStore } = await import('@/store/conversationsStore');
      const { useLojaAtivaStore } = await import('@/store/lojaAtivaStore');
 
      useLojasStore.setState({ fetched: false, lojas: [] });
      useLeadsStore.getState().stopRealtime?.(true); // Para conexões e limpa ledger (B4)
      useLeadsStore.setState({ leads: [] });
      useConversationsStore.setState({ conversations: [] });
      useLojaAtivaStore.setState({ lojasAcessiveis: [], lojaAtivaId: null });
    } catch (e) {
      console.warn('[AUTH] Erro ao resetar stores:', e);
    }

    // 3. Reset the state in the store instantly
    set({
      ...computeDerived(null, null, null),
      loading: false,
      error: null
    });

    // 4. Trigger server signOut in the background as a non-blocking operation
    try {
      supabase.auth.signOut().catch(err => {
        console.warn('[AUTH] Aviso no signOut em background:', err);
      });
    } catch (err) {
      console.warn('[AUTH] Erro ao disparar signOut em background:', err);
    }

    // 5. Redirect immediately
    window.location.href = '/login';
  },

  refreshProfile: async () => {
    const session = get().session;
    if (!session?.user) return;

    const profile = await fetchUserProfile(session.user.id, session.user.email, session.user);
    const tenant = profile?.tenant_id ? await fetchTenant(profile.tenant_id) : null;
    set(computeDerived(session, profile, tenant));
  }
}));

// Clear synchronously on identity changes, including initialize-restored sessions.
useAuthStore.subscribe((state, previous) => {
  const identity = state.session?.user?.id;
  if (identity === previous.session?.user?.id) return;
  useNotificationPreferencesStore.getState().clear();
  // Do not await Auth calls inside Supabase's auth-state callback.
  if (identity) queueMicrotask(() => {
    if (useAuthStore.getState().session?.user?.id === identity) {
      void useNotificationPreferencesStore.getState().load(identity);
    }
  });
});
